import { Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Currencies, MonthlyReport, MonthlyReportIndexStatus } from '../../../../models/monthly-report.model';
import { MonthlyReportService } from '../../../../services/monthly-report.service';
import { DecimalInputComponent } from '../../../common/decimal-input.component';
import { FormsModule } from '@angular/forms';
import { compareAccountNo, formatDecimalTrim, getPool, numericComparator, rateToPercent, toNum, decimalCellRenderer } from '../../../../misc/functions';
import { AdminSearchUserComponent } from '../../search-user/admin-search-user.component';
import { AdminUser } from '../../../../services/admin-users.service';
import { CurrencyTypes, PoolTypeMap } from '../../../../models/weekly-report.model';
import { ModalSnackbarService } from '../../../../services/modal-snackbar.service';
import { AG_GRID_LOCALE_JP } from '../../../../misc/ag-grid-locale';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { CustomTextFilterComponent } from '../../../../misc/custom-filter.component';
import { MatRadioModule } from '@angular/material/radio';
import { WithdrawalTransactionService } from '../../../../services/withdrawal-transaction.service';
import { WithdrawalTransaction } from '../../../../models/withdrawal-transaction.model';

interface DialogData {
  ad: number
};

@Component({
  standalone: true,
  selector: 'app-withdrawal-transaction-create-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatRadioModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    DecimalInputComponent,
    FormsModule,
    AdminSearchUserComponent,
    AgGridModule
  ],
  templateUrl: './admin-withdrawal-transaction-create.dialog.component.html',
})
export class AdminWithdrawalTransactionCreateDialogComponent {

  vfTrim = (p: ValueFormatterParams) => formatDecimalTrim(p.value, 'ja-JP');
  toNum = toNum;
  numericComparator = numericComparator;
  getPool = getPool;
  rateToPercent = rateToPercent;
  compareAccountNo = compareAccountNo;
  Currencies = Currencies;

  toYM(period: string) {
    return period.slice(0, 7);
  }

  defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    autoHeaderHeight: true,
    wrapHeaderText: true,
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressFiltersToolPanel: true,
    floatingFilterComponent: CustomTextFilterComponent,
    editable: false,
    sortable: true
  };

  columnDefs: ColDef[] = [

    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 50,
      pinned: 'left'
    },

    { field: 'period', headerName: '　月次', width: 80, pinned: 'left', valueFormatter: p => this.toYM(p.data.period) },
    { field: 'pool', headerName: 'POOL', width: 95, pinned: 'left', valueFormatter: p => this.getPool(p.data.cap_id), filterValueGetter: p => this.getPool(p.data.cap_id)},
    { field: 'cap_id', headerName: '口座番号', width: 105, pinned: 'left', comparator: this.compareAccountNo },

    { field: 'cur', headerName: '通貨', width: 65 },

    { field: 'shukkin', headerName: '出金額', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.shukkin)  },
    { field: 'is_full_withdrawal', headerName: '全額出金', width: 46, valueGetter: p => p.data.is_full_withdrawal ? '✔' : null, filterValueGetter: p => p.data.is_full_withdrawal ? '✔' : null},

    { field: 'bal_b', headerName: '月末残高', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.bal_b)  },

    { field: 'cancel', headerName: '解約日', width: 100 },
    { field: 'note', headerName: '備考', width: 200 },

    { field: 'enroll', headerName: '運用開始日', width: 100 },
    { field: 'res_b', headerName: '拘束期間', width: 65 },
    { field: 'chg', headerName: '拘束期間終了日', width: 110 },
    { field: 'cmm', headerName: '共同名義者名', width: 100 },
  ];

  gridApi?: GridApi<MonthlyReport>;
  saving = false;

  localeText = AG_GRID_LOCALE_JP;

  PoolTypeMap = PoolTypeMap;
  CurrencyTypes = CurrencyTypes;

  user!: AdminUser;
  report: any = {};
  reports: MonthlyReport[] = [];

  capIds: string[] = [];
  created_at = undefined;
  method = undefined;
  currency = undefined;
  amount = undefined;
  bank_name = undefined;
  bank_branch = undefined;
  branch_number = undefined;
  acc_type = undefined;
  acc_number = undefined;
  holder_type = undefined;
  overseas = '';
  body = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogData,
    private dialogRef: MatDialogRef<AdminWithdrawalTransactionCreateDialogComponent, WithdrawalTransaction | undefined>,
    private fb: FormBuilder,
    private monthlyService: MonthlyReportService,
    private snackBar: ModalSnackbarService,
    private withdrawalService: WithdrawalTransactionService,
  ) {}

  onGridReady(e: GridReadyEvent<MonthlyReport>) {
    this.gridApi = e.api;
  }

  onCancel(): void {
    this.dialogRef.close(); // 何も返さない = 親側では何もしない
  }

  onPoolSelect(e: MatSelectChange) {
    console.log(e.value);
    this.report.pool = e.value.pool;
    this.report.hdc = e.value.hdc;
    this.report.monthly_management_fee = e.value.mng;
    this.report.res_b = e.value.res_b;
  }

  onUserSelect(e: AdminUser) {
    this.user = e;
    this.report.login_user_id = this.user.login;
    this.report.user_name = this.user.name;

    // 口座番号一覧を取得する
    this.monthlyService.getAllCapIdByLoginId(this.report.login_user_id).subscribe({
      next: (res) => {
        this.reports = res;

      },
      error: () => this.snackBar.show('読み込み失敗')
    });
  }

  onRowSelected = () => {
    this.capIds = this.gridApi?.getSelectedRows().map(r => r.cap_id) ?? [];
  };

  get isValid() {
    return this.created_at && this.capIds.length > 0 && this.method && this.currency && this.amount && this.bank_name
      && this.bank_branch && this.branch_number && this.acc_type && this.acc_number && this.holder_type;
  }

  onSave(): void {
    const acc = this.capIds.join(', ');

    const body = `出資口座番号 : ${acc}
出金方法 : ${this.method}
出金通貨 : ${this.currency}
最終確定した出金額 : ${this.amount}
確定日 : ${this.created_at}

送金先銀行名 : ${this.bank_name}
本店・支店名 : ${this.bank_branch}
本店・支店名番号 : ${this.branch_number}

口座種別 : ${this.acc_type}
口座番号 : ${this.acc_number}
口座名義種別 : ${this.holder_type}

海外銀行の場合 : ${this.overseas}

補足 : ${this.body}`;

    let title = acc + ' ・ ' + this.method;
    if (this.amount && parseFloat(this.amount) > 0) {
      title += ' ・ ' + this.amount;
    }

    const record = {
      user_id: this.user.id,
      currency: this.currency,
      requested_amount: this.amount,
      settled_amount: '0',
      unpaid_amount: this.amount,
      created_at: this.created_at,
      alt_body: body
    };

    this.saving = true;

    this.withdrawalService.create(record).subscribe({
      next: (created: WithdrawalTransaction) => {
        this.saving = false;
        this.dialogRef.close(created);
      },
      error: (err: any) => {
        console.error(err);
        this.saving = false;
        // TODO: Snackbar などでエラー表示（既に共通化済ならそこで）
      },
    });
  }

}
